// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAccessibleSubmit from "@insite/client-framework/Common/Hooks/useAccessibleSubmit";
import { HasCartLineContext, withCartLine } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { isOutOfStock } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import translate from "@insite/client-framework/Translate";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import { ButtonProps } from "@insite/mobius/Button";
import { IconProps } from "@insite/mobius/Icon";
import { TextFieldPresentationProps } from "@insite/mobius/TextField";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import QtyField, { QtyFieldStyles } from "@tps/App/Components/Qty/QtyField";
import cartLineSharedStyles from "@tps/App/SharedStyles/CartLineSharedStyles";
import { mergeWithCss, xMask } from "@tps/XDL/Utilities";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    cart: Cart;
    /**
     * If true, the qtyOrdered will display in a text field for editing.
     * If false, the qtyOrdered will display as read-only text.
     * Default value: false
     */
    editable?: boolean;
    onQtyOrderedChange?: (qtyOrdered: number) => void;
    extendedStyles?: QtyFieldStyles;
    label?: string;
}

const mapStateToProps = (state: ApplicationState) => ({
    isLoading: state.data.carts.isLoading,
    cartLoadedTime: state.data.carts.cartLoadedTime,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasCartLineContext;

export interface CartLineQuantityStyles {
    editableQuantityTextField?: TextFieldPresentationProps;
    readOnlyQuantityText?: SmallHeadingAndTextStyles;
    // XNG-Changes
    qtyFieldStyles?: QtyFieldStyles;
}

export const cartLineQuantityStyles: CartLineQuantityStyles = {
    readOnlyQuantityText: {
        ...cartLineSharedStyles.extendedUnitNetPriceText,
    },
    qtyFieldStyles: {},
};

const CartLineQuantity: FC<Props> = ({
    cart,
    cartLine,
    editable = false,
    label,
    isLoading,
    cartLoadedTime,
    onQtyOrderedChange = () => {},
    extendedStyles,
}) => {
    const [styles] = React.useState(() => mergeWithCss(cartLineQuantityStyles, extendedStyles));

    const localOnQtyOrderedChange = (value: string) => {
        const inputQty = parseFloat(value);
        if (Number.isNaN(inputQty)) {
            return;
        }

        onQtyOrderedChange(inputQty);
    };

    // XNG-Changes
    const xChangeHandler = (event: any) => {
        event.currentTarget.value = Number(xMask.qty(event.currentTarget.value));
        changeHandler(event);
    };

    // XNG-Changes
    const xBlurHandler = (event: any) => {
        const value = event?.currentTarget?.value;
        if (value && Number(xMask.qty(value)) === 0) {
            event.currentTarget.value = cartLine.qtyOrdered;
            changeHandler(event);
            return;
        }
        blurHandler();
    };

    const { value, changeHandler, keyDownHandler, blurHandler } = useAccessibleSubmit(
        cartLine.qtyOrdered!.toString(),
        localOnQtyOrderedChange,
        cartLoadedTime,
    );

    const disabled = !cart.canModifyOrder || cartLine.isPromotionItem || cart.type === "Job" || isLoading[cart.id];

    // XNG-Changes use QtyField component
    if (editable && !isOutOfStock(cartLine)) {
        return (
            <QtyField
                onChange={xChangeHandler}
                onKeyDown={keyDownHandler}
                onBlur={xBlurHandler}
                value={value}
                disabled={disabled}
                extendedStyles={styles.qtyFieldStyles}
                data-test-selector="cartline_qty"
                minValue={0}
                showLabel={false}
            />
        );
    }

    return (
        <QtyField
            label={label || translate("QTY")}
            value={cartLine.qtyOrdered!}
            data-test-selector="quoteDetail_cardLine_qty"
            extendedStyles={styles.qtyFieldStyles}
            editable={false}
        />
    );
};

export default connect(mapStateToProps)(withCartLine(CartLineQuantity));
